import React, {useState, useMemo, useRef} from "../../_snowpack/pkg/react.js";
import {useTranslation} from "../../_snowpack/pkg/react-i18next.js";
import {
  FormGroup,
  Select,
  SelectVariant,
  SelectOption,
  PageSection,
  ActionGroup,
  Button,
  Switch,
  ExpandableSection,
  TextInput,
  ButtonVariant,
  InputGroup,
  Toolbar,
  ToolbarGroup,
  ToolbarItem,
  Divider
} from "../../_snowpack/pkg/@patternfly/react-core.js";
import {Controller, useFormContext} from "../../_snowpack/pkg/react-hook-form.js";
import {FormAccess} from "../../components/form-access/FormAccess.js";
import {HelpItem} from "../../components/help-enabler/HelpItem.js";
import {FormPanel} from "../../components/scroll-form/FormPanel.js";
import {useAdminClient, useFetch} from "../../context/auth/AdminClient.js";
import {useRealm} from "../../context/realm-context/RealmContext.js";
import {KeyBasedAttributeInput} from "./KeyBasedAttributeInput.js";
import {defaultContextAttributes} from "../utils.js";
import {TableComposable, Th, Thead, Tr} from "../../_snowpack/pkg/@patternfly/react-table.js";
import "./auth-evaluate.css";
import {AuthorizationEvaluateResource} from "./AuthorizationEvaluateResource.js";
import {SearchIcon} from "../../_snowpack/pkg/@patternfly/react-icons.js";
import {ListEmptyState} from "../../components/list-empty-state/ListEmptyState.js";
import {KeycloakTextInput} from "../../components/keycloak-text-input/KeycloakTextInput.js";
var ResultsFilter;
(function(ResultsFilter2) {
  ResultsFilter2["All"] = "ALL";
  ResultsFilter2["StatusDenied"] = "STATUS_DENIED";
  ResultsFilter2["StatusPermitted"] = "STATUS_PERMITTED";
})(ResultsFilter || (ResultsFilter = {}));
function filterResults(results, filter) {
  switch (filter) {
    case ResultsFilter.StatusPermitted:
      return results.filter(({status}) => status === "PERMIT");
    case ResultsFilter.StatusDenied:
      return results.filter(({status}) => status === "DENY");
    default:
      return results;
  }
}
export const AuthorizationEvaluate = ({client}) => {
  const form = useFormContext();
  const {control, reset, trigger} = form;
  const {t} = useTranslation("clients");
  const adminClient = useAdminClient();
  const realm = useRealm();
  const [clientsDropdownOpen, setClientsDropdownOpen] = useState(false);
  const [scopesDropdownOpen, setScopesDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [applyToResourceType, setApplyToResourceType] = useState(false);
  const [resources, setResources] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [evaluateResults, setEvaluateResults] = useState([]);
  const [showEvaluateResults, setShowEvaluateResults] = useState(false);
  const searchQueryRef = useRef("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [key, setKey] = useState(0);
  const refresh = () => {
    setKey(key + 1);
  };
  const [filter, setFilter] = useState(ResultsFilter.All);
  const [clients, setClients] = useState([]);
  const [clientRoles, setClientRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const filteredResources = useMemo(() => filterResults(evaluateResults, filter).filter(({resource}) => resource?.name?.includes(searchQuery) ?? false), [evaluateResults, filter, searchQuery]);
  useFetch(() => Promise.all([
    adminClient.clients.find(),
    adminClient.roles.find(),
    adminClient.users.find()
  ]), ([clients2, roles, users2]) => {
    setClients(clients2);
    setClientRoles(roles);
    setUsers(users2);
  }, []);
  useFetch(() => Promise.all([
    adminClient.clients.listResources({
      id: client.id
    }),
    adminClient.clients.listAllScopes({
      id: client.id
    })
  ]), ([resources2, scopes2]) => {
    setResources(resources2);
    setScopes(scopes2);
  }, [key, filter]);
  const evaluate = async () => {
    if (!await trigger()) {
      return;
    }
    const formValues = form.getValues();
    const keys = formValues.resources.map(({key: key2}) => key2);
    const resEval = {
      roleIds: formValues.roleIds ?? [],
      clientId: formValues.client.id,
      userId: formValues.user.id,
      resources: formValues.resources.filter((resource) => keys.includes(resource.name)),
      entitlements: false,
      context: {
        attributes: Object.fromEntries(formValues.context.attributes.filter((item) => item.key || item.value !== "").map(({key: key2, value}) => [key2, value]))
      }
    };
    const evaluation = await adminClient.clients.evaluateResource({id: client.id, realm: realm.realm}, resEval);
    setEvaluateResults(evaluation.results);
    setShowEvaluateResults(true);
    return evaluateResults;
  };
  const confirmSearchQuery = () => {
    setSearchQuery(searchQueryRef.current);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      confirmSearchQuery();
    }
  };
  const handleInputChange = (value) => {
    searchQueryRef.current = value;
  };
  const noEvaluatedData = evaluateResults.length === 0;
  const noFilteredData = filteredResources.length === 0;
  return showEvaluateResults ? /* @__PURE__ */ React.createElement(PageSection, null, /* @__PURE__ */ React.createElement(Toolbar, null, /* @__PURE__ */ React.createElement(ToolbarGroup, {
    className: "providers-toolbar"
  }, /* @__PURE__ */ React.createElement(ToolbarItem, null, /* @__PURE__ */ React.createElement(InputGroup, null, /* @__PURE__ */ React.createElement(TextInput, {
    name: "inputGroupName",
    id: "inputGroupName",
    type: "search",
    "aria-label": t("common:search"),
    placeholder: t("common:search"),
    onChange: handleInputChange,
    onKeyDown: handleKeyDown
  }), /* @__PURE__ */ React.createElement(Button, {
    variant: ButtonVariant.control,
    "aria-label": t("common:search"),
    onClick: () => confirmSearchQuery()
  }, /* @__PURE__ */ React.createElement(SearchIcon, null)))), /* @__PURE__ */ React.createElement(ToolbarItem, null, /* @__PURE__ */ React.createElement(Select, {
    width: 300,
    "data-testid": "filter-type-select",
    isOpen: filterDropdownOpen,
    className: "kc-filter-type-select",
    variant: SelectVariant.single,
    onToggle: () => setFilterDropdownOpen(!filterDropdownOpen),
    onSelect: (_, value) => {
      setFilter(value);
      setFilterDropdownOpen(false);
      refresh();
    },
    selections: filter
  }, /* @__PURE__ */ React.createElement(SelectOption, {
    "data-testid": "all-results-option",
    value: ResultsFilter.All,
    isPlaceholder: true
  }, t("allResults")), /* @__PURE__ */ React.createElement(SelectOption, {
    "data-testid": "result-permit-option",
    value: ResultsFilter.StatusPermitted
  }, t("resultPermit")), /* @__PURE__ */ React.createElement(SelectOption, {
    "data-testid": "result-deny-option",
    value: ResultsFilter.StatusDenied
  }, t("resultDeny")))))), !noFilteredData && /* @__PURE__ */ React.createElement(TableComposable, {
    "aria-label": t("evaluationResults")
  }, /* @__PURE__ */ React.createElement(Thead, null, /* @__PURE__ */ React.createElement(Tr, null, /* @__PURE__ */ React.createElement(Th, null), /* @__PURE__ */ React.createElement(Th, null, t("resource")), /* @__PURE__ */ React.createElement(Th, null, t("overallResults")), /* @__PURE__ */ React.createElement(Th, null, t("scopes")), /* @__PURE__ */ React.createElement(Th, null))), filteredResources.map((resource, rowIndex) => /* @__PURE__ */ React.createElement(AuthorizationEvaluateResource, {
    key: rowIndex,
    rowIndex,
    resource,
    evaluateResults
  }))), (noFilteredData || noEvaluatedData) && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(ListEmptyState, {
    isSearchVariant: true,
    message: t("common:noSearchResults"),
    instructions: t("common:noSearchResultsInstructions")
  })), /* @__PURE__ */ React.createElement(ActionGroup, {
    className: "kc-evaluated-options"
  }, /* @__PURE__ */ React.createElement(Button, {
    "data-testid": "authorization-eval",
    id: "back-btn",
    onClick: () => setShowEvaluateResults(false)
  }, t("common:back")), /* @__PURE__ */ React.createElement(Button, {
    "data-testid": "authorization-reevaluate",
    id: "reevaluate-btn",
    variant: "secondary",
    onClick: () => evaluate()
  }, t("clients:reevaluate")), /* @__PURE__ */ React.createElement(Button, {
    "data-testid": "authorization-revert",
    variant: "secondary"
  }, t("showAuthData")))) : /* @__PURE__ */ React.createElement(PageSection, null, /* @__PURE__ */ React.createElement(FormPanel, {
    className: "kc-identity-information",
    title: t("clients:identityInformation")
  }, /* @__PURE__ */ React.createElement(FormAccess, {
    isHorizontal: true,
    role: "view-clients",
    onSubmit: form.handleSubmit(evaluate)
  }, /* @__PURE__ */ React.createElement(FormGroup, {
    label: t("client"),
    isRequired: true,
    labelIcon: /* @__PURE__ */ React.createElement(HelpItem, {
      helpText: "clients-help:client",
      fieldLabelId: "clients:client"
    }),
    fieldId: "client"
  }, /* @__PURE__ */ React.createElement(Controller, {
    name: "client",
    defaultValue: client,
    control,
    render: ({onChange, value}) => /* @__PURE__ */ React.createElement(Select, {
      toggleId: "client",
      onToggle: setClientsDropdownOpen,
      onSelect: (_, value2) => {
        onChange(value2);
        setClientsDropdownOpen(false);
      },
      selections: value.clientId,
      variant: SelectVariant.typeahead,
      "aria-label": t("client"),
      isOpen: clientsDropdownOpen
    }, clients.map((client2) => /* @__PURE__ */ React.createElement(SelectOption, {
      selected: client2.id === value.id,
      key: client2.clientId,
      value: client2
    }, client2.clientId)))
  })), /* @__PURE__ */ React.createElement(FormGroup, {
    label: t("user"),
    isRequired: true,
    labelIcon: /* @__PURE__ */ React.createElement(HelpItem, {
      helpText: "clients-help:userSelect",
      fieldLabelId: "clients:userSelect"
    }),
    fieldId: "user"
  }, /* @__PURE__ */ React.createElement(Controller, {
    name: "user",
    rules: {
      required: true
    },
    defaultValue: "",
    control,
    render: ({onChange, value}) => /* @__PURE__ */ React.createElement(Select, {
      toggleId: "user",
      placeholderText: t("selectAUser"),
      onToggle: setUserDropdownOpen,
      onSelect: (_, value2) => {
        onChange(value2);
        setUserDropdownOpen(false);
      },
      selections: value.username,
      variant: SelectVariant.typeahead,
      "aria-label": t("user"),
      isOpen: userDropdownOpen
    }, users.map((user) => /* @__PURE__ */ React.createElement(SelectOption, {
      selected: user.username === value.username,
      key: user.username,
      value: user
    }, user.username)))
  })), /* @__PURE__ */ React.createElement(FormGroup, {
    label: t("roles"),
    labelIcon: /* @__PURE__ */ React.createElement(HelpItem, {
      helpText: "clients-help:roles",
      fieldLabelId: "clients:roles"
    }),
    fieldId: "realmRole"
  }, /* @__PURE__ */ React.createElement(Controller, {
    name: "roleIds",
    placeholderText: t("selectARole"),
    control,
    defaultValue: [],
    render: ({onChange, value}) => /* @__PURE__ */ React.createElement(Select, {
      variant: SelectVariant.typeaheadMulti,
      toggleId: "role",
      onToggle: setRoleDropdownOpen,
      selections: value,
      onSelect: (_, v) => {
        const option = v.toString();
        if (value.includes(option)) {
          onChange(value.filter((item) => item !== option));
        } else {
          onChange([...value, option]);
        }
        setRoleDropdownOpen(false);
      },
      onClear: (event) => {
        event.stopPropagation();
        onChange([]);
      },
      "aria-label": t("realmRole"),
      isOpen: roleDropdownOpen
    }, clientRoles.map((role) => /* @__PURE__ */ React.createElement(SelectOption, {
      selected: role.name === value,
      key: role.name,
      value: role.name
    })))
  })))), /* @__PURE__ */ React.createElement(FormPanel, {
    className: "kc-permissions",
    title: t("common:permissions")
  }, /* @__PURE__ */ React.createElement(FormAccess, {
    isHorizontal: true,
    role: "view-clients"
  }, /* @__PURE__ */ React.createElement(FormGroup, {
    label: t("applyToResourceType"),
    fieldId: "applyToResourceType",
    labelIcon: /* @__PURE__ */ React.createElement(HelpItem, {
      helpText: "clients-help:applyToResourceType",
      fieldLabelId: "clients:applyToResourceType"
    })
  }, /* @__PURE__ */ React.createElement(Switch, {
    id: "applyToResource-switch",
    label: t("common:on"),
    labelOff: t("common:off"),
    isChecked: applyToResourceType,
    onChange: setApplyToResourceType
  })), !applyToResourceType ? /* @__PURE__ */ React.createElement(FormGroup, {
    label: t("resourcesAndAuthScopes"),
    id: "resourcesAndAuthScopes",
    isRequired: true,
    labelIcon: /* @__PURE__ */ React.createElement(HelpItem, {
      helpText: t("clients-help:contextualAttributes"),
      fieldLabelId: `resourcesAndAuthScopes`
    }),
    helperTextInvalid: t("common:required"),
    fieldId: "resourcesAndAuthScopes"
  }, /* @__PURE__ */ React.createElement(KeyBasedAttributeInput, {
    selectableValues: resources.map((item) => ({
      name: item.name,
      key: item._id
    })),
    resources,
    name: "resources"
  })) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, {
    label: t("resourceType"),
    isRequired: true,
    labelIcon: /* @__PURE__ */ React.createElement(HelpItem, {
      helpText: "clients-help:resourceType",
      fieldLabelId: "clients:resourceType"
    }),
    fieldId: "client"
  }, /* @__PURE__ */ React.createElement(KeycloakTextInput, {
    type: "text",
    id: "alias",
    name: "alias",
    "data-testid": "alias",
    ref: form.register({required: true})
  })), /* @__PURE__ */ React.createElement(FormGroup, {
    label: t("authScopes"),
    labelIcon: /* @__PURE__ */ React.createElement(HelpItem, {
      helpText: "clients-help:scopesSelect",
      fieldLabelId: "clients:client"
    }),
    fieldId: "authScopes"
  }, /* @__PURE__ */ React.createElement(Controller, {
    name: "authScopes",
    defaultValue: [],
    control,
    render: ({onChange, value}) => /* @__PURE__ */ React.createElement(Select, {
      toggleId: "authScopes",
      onToggle: setScopesDropdownOpen,
      onSelect: (_, v) => {
        const option = v.toString();
        if (value.includes(option)) {
          onChange(value.filter((item) => item !== option));
        } else {
          onChange([...value, option]);
        }
        setScopesDropdownOpen(false);
      },
      selections: value,
      variant: SelectVariant.typeaheadMulti,
      "aria-label": t("authScopes"),
      isOpen: scopesDropdownOpen
    }, scopes.map((scope) => /* @__PURE__ */ React.createElement(SelectOption, {
      selected: scope.name === value,
      key: scope.id,
      value: scope.name
    })))
  }))), /* @__PURE__ */ React.createElement(ExpandableSection, {
    toggleText: t("contextualInfo"),
    onToggle: () => setIsExpanded(!isExpanded),
    isExpanded
  }, /* @__PURE__ */ React.createElement(FormGroup, {
    label: t("contextualAttributes"),
    id: "contextualAttributes",
    labelIcon: /* @__PURE__ */ React.createElement(HelpItem, {
      helpText: t("clients-help:contextualAttributes"),
      fieldLabelId: `contextualAttributes`
    }),
    helperTextInvalid: t("common:required"),
    fieldId: "contextualAttributes"
  }, /* @__PURE__ */ React.createElement(KeyBasedAttributeInput, {
    selectableValues: defaultContextAttributes,
    name: "context.attributes"
  })))), /* @__PURE__ */ React.createElement(ActionGroup, null, /* @__PURE__ */ React.createElement(Button, {
    "data-testid": "authorization-eval",
    isDisabled: form.getValues().resources?.every((e) => e.key === ""),
    onClick: () => evaluate()
  }, t("evaluate")), /* @__PURE__ */ React.createElement(Button, {
    "data-testid": "authorization-revert",
    variant: "link",
    onClick: () => reset()
  }, t("common:revert")), /* @__PURE__ */ React.createElement(Button, {
    "data-testid": "authorization-revert",
    variant: "primary",
    onClick: () => reset(),
    isDisabled: true
  }, t("lastEvaluation")))));
};
